'use client';

import * as React from 'react';
import { MenuRadioGroupContext } from "./MenuRadioGroupContext.js";
import { useComponentRenderer } from "../../utils/useComponentRenderer.js";
import { useControlled } from "../../utils/useControlled.js";
import { useEventCallback } from "../../utils/useEventCallback.js";

/**
 * Groups related radio items.
 * Renders a `<div>` element.
 *
 * Documentation: [Base UI Menu](https://base-ui.com/react/components/menu)
 */
import { jsx as _jsx } from "react/jsx-runtime";
export const MenuRadioGroup = /*#__PURE__*/React.memo(/*#__PURE__*/React.forwardRef(function MenuRadioGroup(props, forwardedRef) {
  const {
    render,
    className,
    value: valueProp,
    defaultValue,
    onValueChange: onValueChangeProp,
    disabled = false,
    ...other
  } = props;
  const [value, setValueUnwrapped] = useControlled({
    controlled: valueProp,
    default: defaultValue,
    name: 'MenuRadioGroup'
  });
  const onValueChange = useEventCallback(onValueChangeProp);
  const setValue = React.useCallback((newValue, event) => {
    setValueUnwrapped(newValue);
    onValueChange?.(newValue, event);
  }, [onValueChange, setValueUnwrapped]);
  const state = React.useMemo(() => ({
    disabled
  }), [disabled]);
  const {
    renderElement
  } = useComponentRenderer({
    render: render || 'div',
    className,
    state,
    extraProps: {
      role: 'group',
      'aria-disabled': disabled || undefined,
      ...other
    },
    ref: forwardedRef
  });
  const context = React.useMemo(() => ({
    value,
    setValue,
    disabled
  }), [value, setValue, disabled]);
  return /*#__PURE__*/_jsx(MenuRadioGroupContext.Provider, {
    value: context,
    children: renderElement()
  });
}));
if (process.env.NODE_ENV !== "production") MenuRadioGroup.displayName = "MenuRadioGroup";